import React, { useState, useEffect, useMemo } from 'react';
import { ChevronDown, Download, Loader2, X, FileText } from 'lucide-react';
import KPISnapshot from '../components/KPISnapshot';
import RiskRadarChart from '../components/RiskRadarChart';
import NewsTicker from '../components/NewsTicker';
import { getCasConsolidatedSummary } from '../services/api';
import { getKpiSnapshotData, getAvailableYears } from '../services/kpiSnapshotApi';
import { jsPDF } from 'jspdf';

const MINERAL_OPTIONS = [
  'All',
  'Lithium',
  'Cobalt',
  'Nickel',
  'Graphite',
  'Rare Earth Elements',
  'Critical minerals',
];

/** Split recommendation_actions string into list items (newlines, semicolons, or bullet markers). */
function recommendationActionsList(text) {
  if (!text || typeof text !== 'string') return [];
  return text
    .split(/\n|;|(?:\s*[-*•]\s+)/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function clampPercent(value) {
  if (typeof value !== 'number' || Number.isNaN(value)) return value;
  return Math.min(100, Math.abs(value));
}

const OverviewPage = () => {
  const [consolidated, setConsolidated] = useState({ summary: '', detailed_analysis: '', recommendation_actions: '' });
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState(null);
  const [summaryNotice, setSummaryNotice] = useState(null);
  const [selectedMineral, setSelectedMineral] = useState('All');
  const [showMineralDropdown, setShowMineralDropdown] = useState(false);
  const [kpiYear, setKpiYear] = useState(null);

  const [showPageSummary, setShowPageSummary] = useState(false);
  const [pageSummaryLoading, setPageSummaryLoading] = useState(false);
  const [pageSummaryError, setPageSummaryError] = useState(null);
  const [pageSummaryKpi, setPageSummaryKpi] = useState(null);
  const [pageSummaryRadar, setPageSummaryRadar] = useState(null);
  const [pageSummaryRadarYear, setPageSummaryRadarYear] = useState(null);
  const [pageSummaryRadarYears, setPageSummaryRadarYears] = useState([]);
  const [pageSummaryRadarMinerals, setPageSummaryRadarMinerals] = useState([]);

  // Keep KPI year in sync with available years (default to first)
  useEffect(() => {
    let cancelled = false;
    getAvailableYears()
      .then((res) => {
        if (cancelled) return;
        const years = res?.success ? res.data : res;
        if (!kpiYear && Array.isArray(years) && years.length > 0) setKpiYear(years[0]);
      })
      .catch(() => {})
      .finally(() => {});
    return () => { cancelled = true; };
  }, [kpiYear]);

  useEffect(() => {
    let cancelled = false;
    setSummaryLoading(true);
    setSummaryError(null);
    setSummaryNotice(null);
    getCasConsolidatedSummary(selectedMineral)
      .then((res) => {
        if (!cancelled && res) {
          setConsolidated({
            summary: res.summary || '',
            detailed_analysis: res.detailed_analysis || '',
            recommendation_actions: res.recommendation_actions || '',
          });
          const hasData = Boolean(res.summary || res.detailed_analysis || res.recommendation_actions);
          if (res.error && !hasData) {
            setSummaryError(res.error);
          } else {
            setSummaryError(null);
          }

          if (res.error && hasData) {
            setSummaryNotice(`Showing cached data: ${res.error}`);
          } else if (res.stale || res.message) {
            const msg = res.message || '';
            if (msg.trim() === 'Showing last successful consolidated summary from cache.') {
              setSummaryNotice(null);
            } else {
              setSummaryNotice(msg || null);
            }
          } else {
            setSummaryNotice(null);
          }
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setSummaryError(err?.message || 'Failed to load summary');
          setConsolidated({ summary: '', detailed_analysis: '', recommendation_actions: '' });
        }
      })
      .finally(() => {
        if (!cancelled) setSummaryLoading(false);
      });
    return () => { cancelled = true; };
  }, [selectedMineral]);

  const downloadPdf = () => {
    const doc = new jsPDF({ format: 'a4', unit: 'mm' });
    const title = `Executive Summary${selectedMineral && selectedMineral !== 'All' ? ` - ${selectedMineral}` : ''}`;
    doc.setFontSize(16);
    doc.text(title, 14, 20);
    doc.setFontSize(10);
    let y = 28;
    const lineHeight = 6;
    const margin = 14;
    const pageWidth = doc.internal.pageSize.getWidth();
    const maxWidth = pageWidth - margin * 2;

    if (consolidated.summary) {
      doc.setFont(undefined, 'bold');
      doc.text('Summary', margin, y);
      y += lineHeight;
      doc.setFont(undefined, 'normal');
      const summaryLines = doc.splitTextToSize(consolidated.summary, maxWidth);
      doc.text(summaryLines, margin, y);
      y += lineHeight * summaryLines.length + 4;
    }
    if (consolidated.detailed_analysis) {
      if (y > 250) { doc.addPage(); y = 20; }
      doc.setFont(undefined, 'bold');
      doc.text('Detailed Analysis', margin, y);
      y += lineHeight;
      doc.setFont(undefined, 'normal');
      const detailLines = doc.splitTextToSize(consolidated.detailed_analysis, maxWidth);
      doc.text(detailLines, margin, y);
      y += lineHeight * detailLines.length + 4;
    }
    if (consolidated.recommendation_actions) {
      if (y > 250) { doc.addPage(); y = 20; }
      doc.setFont(undefined, 'bold');
      doc.text('Recommendation Actions', margin, y);
      y += lineHeight;
      doc.setFont(undefined, 'normal');
      const recItems = recommendationActionsList(consolidated.recommendation_actions);
      recItems.forEach((item) => {
        if (y > 270) { doc.addPage(); y = 20; }
        const bullet = '\u2022 ';
        const lines = doc.splitTextToSize(bullet + item, maxWidth);
        doc.text(lines, margin, y);
        y += lineHeight * lines.length;
      });
    }

    doc.save(`Executive-Summary-${selectedMineral === 'All' ? 'All' : selectedMineral.replace(/\s+/g, '-')}.pdf`);
  };

  const selectedMineralKey = useMemo(() => (selectedMineral || 'All'), [selectedMineral]);

  const fetchPageSummaryRadar = async (radarYear, mineralsList) => {
    if (!radarYear) {
      setPageSummaryRadar(null);
      return;
    }
    if (selectedMineralKey !== 'All') {
      const resp = await fetch(
        `/api/risk-radar?mineral=${encodeURIComponent(selectedMineralKey)}&year=${encodeURIComponent(radarYear)}`
      );
      setPageSummaryRadar(resp.ok ? await resp.json() : null);
      return;
    }
    const minerals = mineralsList || pageSummaryRadarMinerals || [];
    const all = [];
    for (const m of minerals) {
      try {
        const resp = await fetch(`/api/risk-radar?mineral=${encodeURIComponent(m)}&year=${encodeURIComponent(radarYear)}`);
        if (resp.ok) all.push(await resp.json());
      } catch (_) {}
    }
    setPageSummaryRadar(all);
  };

  const openPageSummary = async () => {
    setShowPageSummary(true);
    setPageSummaryLoading(true);
    setPageSummaryError(null);
    try {
      // KPI snapshot for current year
      const yearToUse = kpiYear;
      const kpiRes = await getKpiSnapshotData(yearToUse);
      const kpiData = kpiRes?.success ? kpiRes.data : kpiRes;
      setPageSummaryKpi(kpiData || null);

      // Risk radar: choose latest available year and fetch mineral-specific data
      const optsResp = await fetch('/api/risk-radar/options');
      const opts = optsResp.ok ? await optsResp.json() : null;
      const years = opts?.years || [];
      const minerals = opts?.minerals || [];
      setPageSummaryRadarYears(years);
      setPageSummaryRadarMinerals(minerals);

      // Default Risk Radar year should match KPI year (if available in radar years), else fall back to latest.
      const kpiYearStr = kpiYear != null ? String(kpiYear) : null;
      const radarYear =
        (kpiYearStr && years.map(String).includes(kpiYearStr) ? kpiYearStr : (years && years.length > 0 ? String(years[0]) : null));
      setPageSummaryRadarYear(radarYear);
      await fetchPageSummaryRadar(radarYear, minerals);
    } catch (e) {
      setPageSummaryError(e?.message || 'Failed to build page summary');
    } finally {
      setPageSummaryLoading(false);
    }
  };

  // If KPI year changes while modal is open, keep Risk Radar year aligned (unless user manually changed it)
  const [pageSummaryRadarYearManuallySet, setPageSummaryRadarYearManuallySet] = useState(false);
  useEffect(() => {
    if (!showPageSummary) return;
    if (pageSummaryRadarYearManuallySet) return;
    if (!kpiYear) return;
    const desired = String(kpiYear);
    if (pageSummaryRadarYears.map(String).includes(desired) && pageSummaryRadarYear !== desired) {
      setPageSummaryRadarYear(desired);
      fetchPageSummaryRadar(desired);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kpiYear, showPageSummary, pageSummaryRadarYearManuallySet, pageSummaryRadarYears]);

  const downloadPageSummaryPdf = () => {
    const doc = new jsPDF({ format: 'a4', unit: 'mm' });
    const title = `Overview Summary${selectedMineralKey !== 'All' ? ` - ${selectedMineralKey}` : ''}`;
    doc.setFontSize(16);
    doc.text(title, 14, 18);
    doc.setFontSize(10);
    let y = 26;
    const lineHeight = 6;
    const margin = 14;
    const pageWidth = doc.internal.pageSize.getWidth();
    const maxWidth = pageWidth - margin * 2;

    const addSectionTitle = (t) => {
      if (y > 270) { doc.addPage(); y = 20; }
      doc.setFont(undefined, 'bold');
      doc.text(t, margin, y);
      doc.setFont(undefined, 'normal');
      y += lineHeight;
    };

    // KPI snapshot
    addSectionTitle(`KPI Snapshot (Year ${kpiYear || '—'})`);
    const minerals = pageSummaryKpi?.minerals || [];
    const list = selectedMineralKey === 'All'
      ? minerals
      : minerals.filter((m) => String(m?.name || '').toLowerCase().includes(String(selectedMineralKey).toLowerCase().split(' ')[0]));
    list.slice(0, selectedMineralKey === 'All' ? 10 : 1).forEach((m) => {
      const lines = [
        `Mineral: ${m?.name || ''}`,
        `Import dependency: ${
          typeof m?.importDependency === 'number'
            ? clampPercent(m.importDependency).toFixed(2)
            : (m?.importDependency ?? '')
        }% (Risk: ${m?.riskLevel || ''})`,
        `Top partners: ${m?.topSupplier || ''} ${m?.topSharePct ?? ''}% • ${m?.secondSupplier || 'Other'} ${m?.secondSharePct ?? ''}% • ${m?.thirdSupplier || 'Other'} ${m?.thirdSharePct ?? ''}%`,
        `India reserves: ${m?.reservesIndiaT ?? 0} t | India production: ${m?.productionIndiaT ?? 0} t`,
        `Import growth: ${m?.importGrowth ?? 'N/A'}% | Price trend: ${m?.priceTrend ?? 'N/A'}%`,
        '',
      ];
      const wrapped = doc.splitTextToSize(lines.join('\n'), maxWidth);
      if (y + wrapped.length * 4 > 270) { doc.addPage(); y = 20; }
      doc.text(wrapped, margin, y);
      y += wrapped.length * 4 + 2;
    });

    // Executive Summary
    addSectionTitle('Executive Summary');
    if (consolidated.summary) {
      const s = doc.splitTextToSize(consolidated.summary, maxWidth);
      doc.text(s, margin, y); y += lineHeight * s.length + 2;
    }
    if (consolidated.detailed_analysis) {
      const d = doc.splitTextToSize(consolidated.detailed_analysis, maxWidth);
      doc.text(d, margin, y); y += lineHeight * d.length + 2;
    }
    if (consolidated.recommendation_actions) {
      const items = recommendationActionsList(consolidated.recommendation_actions);
      items.forEach((it) => {
        if (y > 270) { doc.addPage(); y = 20; }
        const lines = doc.splitTextToSize(`• ${it}`, maxWidth);
        doc.text(lines, margin, y);
        y += lineHeight * lines.length;
      });
      y += 2;
    }

    // Risk radar
    addSectionTitle(`Risk Radar${pageSummaryRadarYear ? ` (${pageSummaryRadarYear})` : ''}`);
    const writeRadar = (r) => {
      const lines = [
        `Mineral: ${r?.mineral_name || ''}`,
        `Overall: ${r?.overall_risk ?? ''}`,
        `Supply: ${r?.supply_risk ?? ''} | Price: ${r?.price_risk ?? ''} | Political: ${r?.political_risk ?? ''} | Concentration: ${r?.concentration_risk ?? ''}`,
        '',
      ];
      const wrapped = doc.splitTextToSize(lines.join('\n'), maxWidth);
      if (y + wrapped.length * 4 > 270) { doc.addPage(); y = 20; }
      doc.text(wrapped, margin, y);
      y += wrapped.length * 4 + 2;
    };
    if (Array.isArray(pageSummaryRadar)) pageSummaryRadar.forEach(writeRadar);
    else if (pageSummaryRadar) writeRadar(pageSummaryRadar);
    else {
      const none = doc.splitTextToSize('No risk radar data available.', maxWidth);
      doc.text(none, margin, y);
    }

    doc.save(`Overview-Summary-${selectedMineralKey === 'All' ? 'All' : selectedMineralKey.replace(/\\s+/g, '-')}.pdf`);
  };

  const hasContent = consolidated.summary || consolidated.detailed_analysis || consolidated.recommendation_actions;

  const mineralDropdown = (
    <div className="relative">
      <button
        type="button"
        onClick={() => setShowMineralDropdown(!showMineralDropdown)}
        className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
      >
        <span>{selectedMineral}</span>
        <ChevronDown className="h-4 w-4" />
      </button>
      {showMineralDropdown && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setShowMineralDropdown(false)} aria-hidden="true" />
          <div className="absolute right-0 mt-1 w-48 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg z-20">
            {MINERAL_OPTIONS.map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => { setSelectedMineral(m); setShowMineralDropdown(false); }}
                className={`w-full text-left px-3 py-2 text-sm first:rounded-t-lg last:rounded-b-lg ${
                  m === selectedMineral ? 'bg-primary-600 text-white' : 'hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200'
                }`}
              >
                {m}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );

  const executiveSummaryBox = (
    <div className="flex flex-col h-full min-w-0 w-full border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 overflow-hidden">
      <div className="flex items-center justify-between gap-2 p-3 border-b border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/80">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Executive Summary{selectedMineral !== 'All' ? ` – ${selectedMineral}` : ''}</h3>
        <button
          type="button"
          onClick={downloadPdf}
          disabled={!hasContent}
          className="inline-flex items-center gap-1 px-2 py-1.5 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded text-xs font-medium"
        >
          <Download className="h-3.5 w-3.5" />
          PDF
        </button>
      </div>
      <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
        {summaryLoading ? (
          <div className="flex items-center justify-center py-8 flex-1">
            <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
            <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">Analysing Price, Trade, Reserve & News...</span>
          </div>
        ) : summaryError && !hasContent ? (
          <div className="py-6 px-3 text-center text-sm text-amber-600 dark:text-amber-400 flex-1">{summaryError}</div>
        ) : !hasContent ? (
          <div className="py-8 text-center text-sm text-gray-500 dark:text-gray-400 flex-1">
            No Price, Trade, Reserve or News data for {selectedMineral === 'All' ? 'this period' : selectedMineral}. Run the CAS pipeline to populate.
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto p-3 space-y-4 max-h-[420px] text-left">
            {summaryNotice && (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
                {summaryNotice}
              </div>
            )}
            <div>
              <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">Summary</h4>
              <p className="text-sm text-gray-700 dark:text-gray-200 whitespace-pre-wrap">{consolidated.summary}</p>
            </div>
            <div>
              <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">Detailed Analysis</h4>
              <p className="text-sm text-gray-700 dark:text-gray-200 whitespace-pre-wrap">{consolidated.detailed_analysis}</p>
            </div>
            <div>
              <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">Recommendation Actions</h4>
              <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 dark:text-gray-200">
                {recommendationActionsList(consolidated.recommendation_actions).map((action, i) => (
                  <li key={i}>{action}</li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="bg-white dark:bg-gray-900 text-gray-900 dark:text-white w-full page-container transition-colors duration-200">
      <NewsTicker />
      <div className="w-full px-4 pt-4 pb-8">
        <div className="space-y-6">
          <div className="flex justify-end">
            <button
              type="button"
              onClick={openPageSummary}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 dark:bg-gray-700 text-white rounded-lg hover:bg-gray-800 dark:hover:bg-gray-600 text-sm font-medium"
            >
              <FileText className="h-4 w-4" />
              View Summary
            </button>
          </div>

          {/* Single mineral: card left, Executive Summary right (red area). All: cards then summary below */}
          <section>
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-600 transition-colors duration-200">
              <h2 className="text-3xl font-bold mb-6 text-gray-900 dark:text-white">Import Dependency and Risk Profile for Critical Minerals</h2>
              <p className="mb-6 text-gray-900 dark:text-white">
              Provides an overview of critical mineral dependency, trade exposure, domestic reserves, and overall market risk. It helps identify supply-chain vulnerabilities and track progress toward self-reliance.
              </p>
              {selectedMineral !== 'All' ? (
                <div className="flex gap-4 items-stretch">
                  <div className="min-w-0 flex-shrink-0">
                    <KPISnapshot
                      headerExtra={mineralDropdown}
                      filterMineral={selectedMineral}
                      selectedYear={kpiYear}
                      onSelectedYearChange={setKpiYear}
                    />
                  </div>
                  <div className="flex-1 min-w-0 flex flex-col">
                    {executiveSummaryBox}
                  </div>
                </div>
              ) : (
                <>
                  <KPISnapshot
                    headerExtra={mineralDropdown}
                    filterMineral={null}
                    selectedYear={kpiYear}
                    onSelectedYearChange={setKpiYear}
                  />
                </>
              )}
            </div>
            {selectedMineral === 'All' && (
              <div className="mt-6">
                {executiveSummaryBox}
              </div>
            )}
          </section>

          {/* Risk Radar Chart Section - Full Width Below */}
          <section>
            <RiskRadarChart />
          </section>
        </div>
      </div>

      {showPageSummary && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => !pageSummaryLoading && setShowPageSummary(false)} />
          <div className="relative z-10 w-[min(960px,95vw)] max-h-[90vh] overflow-hidden rounded-xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-xl">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/80">
              <div className="min-w-0">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                  Overview Summary{selectedMineralKey !== 'All' ? ` – ${selectedMineralKey}` : ''}
                </h3>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  Includes KPI Snapshot, Executive Summary, and Risk Radar{pageSummaryRadarYear ? ` (${pageSummaryRadarYear})` : ''}
                </p>
              </div>
              <button
                type="button"
                onClick={() => !pageSummaryLoading && setShowPageSummary(false)}
                className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                aria-label="Close"
              >
                <X className="h-4 w-4 text-gray-700 dark:text-gray-200" />
              </button>
            </div>

            <div className="p-4 overflow-y-auto max-h-[calc(90vh-120px)] space-y-6">
              {pageSummaryLoading ? (
                <div className="flex items-center justify-center py-10">
                  <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
                  <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">Building summary…</span>
                </div>
              ) : pageSummaryError ? (
                <div className="text-sm text-amber-600 dark:text-amber-400">{pageSummaryError}</div>
              ) : (
                <>
                  <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                    <div className="px-3 py-2 bg-gray-50 dark:bg-gray-800/60 border-b border-gray-200 dark:border-gray-700">
                      <h4 className="text-xs font-semibold text-gray-800 dark:text-gray-200">KPI Snapshot (Year {kpiYear || '—'})</h4>
                    </div>
                    <div className="p-3 space-y-3 text-sm text-gray-700 dark:text-gray-200">
                      {(() => {
                        const minerals = pageSummaryKpi?.minerals || [];
                        const showAll = selectedMineralKey === 'All';
                        const matches = (m) => {
                          const name = String(m?.name || '').toLowerCase();
                          const f = String(selectedMineralKey || '').toLowerCase();
                          if (!f || f === 'all') return true;
                          if (name === f) return true;
                          if (name.includes(f) || f.includes(name)) return true;
                          if (f.includes('rare') && name.includes('rare')) return true;
                          return false;
                        };
                        const list = showAll ? minerals : minerals.filter(matches);
                        if (!list || list.length === 0) return <div className="text-gray-500 dark:text-gray-400">No KPI data available.</div>;
                        const toShow = showAll ? list : list.slice(0, 1);
                        return (
                          <div className="space-y-3">
                            {toShow.map((m) => (
                              <div key={m?.name || Math.random()} className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 bg-white dark:bg-gray-900">
                                <div className="font-semibold text-gray-900 dark:text-white">{m?.name}</div>
                                <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                                  <div>
                                    <span className="text-gray-500 dark:text-gray-400">Import dependency:</span>{' '}
                                    {typeof m?.importDependency === 'number'
                                      ? clampPercent(m.importDependency).toFixed(2)
                                      : (m?.importDependency ?? '—')}%
                                  </div>
                                  <div><span className="text-gray-500 dark:text-gray-400">Risk level:</span> {m?.riskLevel || '—'}</div>
                                  <div className="md:col-span-2">
                                    <span className="text-gray-500 dark:text-gray-400">Top partners:</span> {m?.topSupplier || '—'} {m?.topSharePct ?? '—'}% • {m?.secondSupplier || 'Other'} {m?.secondSharePct ?? '—'}% • {m?.thirdSupplier || 'Other'} {m?.thirdSharePct ?? '—'}%
                                  </div>
                                  <div><span className="text-gray-500 dark:text-gray-400">India reserves:</span> {m?.reservesIndiaT ?? 0} t</div>
                                  <div><span className="text-gray-500 dark:text-gray-400">India production:</span> {m?.productionIndiaT ?? 0} t</div>
                                  <div><span className="text-gray-500 dark:text-gray-400">Import growth:</span> {m?.importGrowth ?? 'N/A'}%</div>
                                  <div><span className="text-gray-500 dark:text-gray-400">Price trend:</span> {m?.priceTrend ?? 'N/A'}%</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                    </div>
                  </div>

                  <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                    <div className="px-3 py-2 bg-gray-50 dark:bg-gray-800/60 border-b border-gray-200 dark:border-gray-700">
                      <h4 className="text-xs font-semibold text-gray-800 dark:text-gray-200">Executive Summary</h4>
                    </div>
                    <div className="p-3 space-y-3 text-sm text-gray-700 dark:text-gray-200">
                      <div>
                        <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Summary</div>
                        <div className="whitespace-pre-wrap">{consolidated.summary || '—'}</div>
                      </div>
                      <div>
                        <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Detailed Analysis</div>
                        <div className="whitespace-pre-wrap">{consolidated.detailed_analysis || '—'}</div>
                      </div>
                      <div>
                        <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Recommendation Actions</div>
                        {consolidated.recommendation_actions ? (
                          <ul className="list-disc list-inside space-y-1">
                            {recommendationActionsList(consolidated.recommendation_actions).map((action, i) => (
                              <li key={i}>{action}</li>
                            ))}
                          </ul>
                        ) : (
                          <div>—</div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                    <div className="px-3 py-2 bg-gray-50 dark:bg-gray-800/60 border-b border-gray-200 dark:border-gray-700">
                      <div className="flex items-center justify-between gap-3">
                        <h4 className="text-xs font-semibold text-gray-800 dark:text-gray-200">
                          Risk Radar{pageSummaryRadarYear ? ` (${pageSummaryRadarYear})` : ''}
                        </h4>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500 dark:text-gray-400">Year</span>
                          <select
                            value={pageSummaryRadarYear || ''}
                            onChange={async (e) => {
                              const next = e.target.value || null;
                              setPageSummaryRadarYearManuallySet(true);
                              setPageSummaryRadarYear(next);
                              setPageSummaryLoading(true);
                              try {
                                await fetchPageSummaryRadar(next);
                              } catch (err) {
                                setPageSummaryError(err?.message || 'Failed to refresh risk radar');
                              } finally {
                                setPageSummaryLoading(false);
                              }
                            }}
                            className="bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-xs text-gray-900 dark:text-gray-100"
                          >
                            <option value="">—</option>
                            {pageSummaryRadarYears.map((y) => (
                              <option key={String(y)} value={String(y)}>{String(y)}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                    <div className="p-3 text-sm text-gray-700 dark:text-gray-200">
                      {Array.isArray(pageSummaryRadar) ? (
                        <div className="space-y-2">
                          {pageSummaryRadar.map((r) => (
                            <div key={`${r?.mineral_name}-${r?.year || pageSummaryRadarYear}`} className="rounded border border-gray-200 dark:border-gray-700 p-3 bg-white dark:bg-gray-900">
                              <div className="font-semibold text-gray-900 dark:text-white">{r?.mineral_name}</div>
                              <div className="mt-2 grid grid-cols-2 gap-2">
                                <div><span className="text-gray-500 dark:text-gray-400">Supply:</span> {r?.supply_risk ?? '—'}</div>
                                <div><span className="text-gray-500 dark:text-gray-400">Price:</span> {r?.price_risk ?? '—'}</div>
                                <div><span className="text-gray-500 dark:text-gray-400">Political:</span> {r?.political_risk ?? '—'}</div>
                                <div><span className="text-gray-500 dark:text-gray-400">Concentration:</span> {r?.concentration_risk ?? '—'}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : pageSummaryRadar ? (
                        <div className="rounded border border-gray-200 dark:border-gray-700 p-3 bg-white dark:bg-gray-900">
                          <div className="font-semibold text-gray-900 dark:text-white">{pageSummaryRadar?.mineral_name}</div>
                          <div className="mt-2 grid grid-cols-2 gap-2">
                            <div><span className="text-gray-500 dark:text-gray-400">Supply:</span> {pageSummaryRadar?.supply_risk ?? '—'}</div>
                            <div><span className="text-gray-500 dark:text-gray-400">Price:</span> {pageSummaryRadar?.price_risk ?? '—'}</div>
                            <div><span className="text-gray-500 dark:text-gray-400">Political:</span> {pageSummaryRadar?.political_risk ?? '—'}</div>
                            <div><span className="text-gray-500 dark:text-gray-400">Concentration:</span> {pageSummaryRadar?.concentration_risk ?? '—'}</div>
                          </div>
                        </div>
                      ) : (
                        <div className="text-gray-500 dark:text-gray-400">No risk radar data available.</div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/80">
              <button
                type="button"
                onClick={() => setShowPageSummary(false)}
                disabled={pageSummaryLoading}
                className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm font-medium disabled:opacity-50"
              >
                Close
              </button>
              <button
                type="button"
                onClick={downloadPageSummaryPdf}
                disabled={pageSummaryLoading}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium disabled:opacity-50"
              >
                <Download className="h-4 w-4" />
                Download PDF
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OverviewPage;
